import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
// import { MENU_BOOK_LIST } from './mockdata';
import { CommonResponse } from 'models';
import { MenuBook } from './models';
import { MultiSelectToArg } from './types';
import { multiSelectTo } from './utils';

export interface TimeFrameMenuBookState {
  loading: boolean;
  data: MenuBook[];
  selectedItemIds: number[];
  selectedDroppableId?: string;
  draggingItemId?: number;
}

const initialState: TimeFrameMenuBookState = {
  loading: false,
  data: [],
  selectedItemIds: [],
};

const timeFrameMenuBookSlice = createSlice({
  name: 'timeframe_menu_book',
  initialState,
  reducers: {
    fetchTimeFrameMenuBookList(state, action: PayloadAction<{ timeframeId: number }>) {
      state.loading = true;
    },
    fetchTimeFrameMenuBookListSuccess(state, action: PayloadAction<CommonResponse<MenuBook[]>>) {
      state.data = action.payload.data;
      state.loading = false;
    },
    fetchTimeFrameMenuBookListFailed(state) {
      state.loading = false;
    },
    unselectAll(state) {
      state.selectedItemIds = [];
      state.draggingItemId = undefined;
      state.selectedDroppableId = undefined;
    },
    setDraggingItemId(state, action: PayloadAction<number | undefined>) {
      state.draggingItemId = action.payload;
    },
    updateState(state, action: PayloadAction<Partial<TimeFrameMenuBookState>>) {
      state.data = action.payload.data || [];
      state.selectedItemIds = action.payload.selectedItemIds || [];
      state.draggingItemId = action.payload.draggingItemId;
    },
    toggleSelection(state, action: PayloadAction<{ selectedDroppableId: string; itemId: number }>) {
      const selectedItemIds = state.selectedItemIds;
      const wasSelected: boolean =
        state.selectedDroppableId === action.payload.selectedDroppableId &&
        selectedItemIds.includes(action.payload.itemId);

      const newItemIds: number[] = (() => {
        // Task was not previously selected
        // now will be the only selected item
        if (!wasSelected) {
          return [action.payload.itemId];
        }

        // Task was part of a selected group
        // will now become the only selected item
        if (selectedItemIds.length > 1) {
          return [action.payload.itemId];
        }

        // task was previously selected but not in a group
        // we will now clear the selection
        return [];
      })();

      state.selectedDroppableId = action.payload.selectedDroppableId;
      state.selectedItemIds = newItemIds;
    },

    toggleSelectionInGroup(state, action: PayloadAction<{ selectedDroppableId: string; itemId: number }>) {
      const selectedItemIds = state.selectedItemIds;
      const index: number = selectedItemIds.indexOf(action.payload.itemId);

      // if not selected - add it to the selected items
      if (index === -1) {
        state.selectedItemIds = [...selectedItemIds, action.payload.itemId];
        return;
      }

      // it was previously selected and now needs to be removed from the group
      const shallow = [...selectedItemIds];
      shallow.splice(index, 1);
      state.selectedItemIds = shallow;

      state.selectedDroppableId = action.payload.selectedDroppableId;
    },

    multiSelectTo(state, action: PayloadAction<{ arg: MultiSelectToArg; newItemId: number }>) {
      const updated = multiSelectTo(action.payload.arg, action.payload.newItemId);

      if (updated == null) {
        return;
      }
      state.selectedItemIds = updated;
    },
  },
});

// Actions
export const timeFrameMenuBookActions = timeFrameMenuBookSlice.actions;

// Selectors
export const selectTimeFrameMenuBookLoading = (state: RootState) => state.timeFrameMenuBook.loading;
export const selectTimeFrameMenuBookSelectedDroppableId = (state: RootState) =>
  state.timeFrameMenuBook.selectedDroppableId;
export const selectTimeFrameMenuBookSelectedItemIds = (state: RootState) => state.timeFrameMenuBook.selectedItemIds;
export const selectTimeFrameMenuBookDraggingItemId = (state: RootState) => state.timeFrameMenuBook.draggingItemId;
export const selectTimeFrameMenuBookData = (state: RootState) => state.timeFrameMenuBook.data;

export const selectTimeFrameMenuBookSelectedItemIdMap = createSelector(
  selectTimeFrameMenuBookSelectedItemIds,
  (selectedItemIds) => {
    return selectedItemIds.reduce((previous: { [id: number]: true }, current: number): { [id: number]: true } => {
      previous[current] = true;
      return previous;
    }, {});
  }
);

// Reducer
export const timeFrameMenuBookReducer = timeFrameMenuBookSlice.reducer;
